/**
 * Shopkeeper-authored storefront pages.
 *
 * Mirrors `services/api/src/site/dto/site-content.dto.ts`. The seven keys are
 * FROZEN in a database CHECK that cannot be altered (the table is
 * avnadmin-owned), so this list is the complete set for the life of the table —
 * an eighth would produce a 23514 no user could act on. The API serves the same
 * list from `GET /site/content/keys` for exactly that reason; this constant is
 * the fallback and the source of the labels.
 */
export const SITE_PAGE_KEYS = [
  'about',
  'contact',
  'delivery',
  'returns',
  'faq',
  'terms',
  'privacy',
] as const;

export type SitePageKey = (typeof SITE_PAGE_KEYS)[number];

export const SITE_PAGE_LABELS: Record<SitePageKey, string> = {
  about: 'About us',
  contact: 'Contact',
  delivery: 'Delivery',
  returns: 'Returns',
  faq: 'FAQ',
  terms: 'Terms',
  privacy: 'Privacy',
};

/** What each page is for, so a shopkeeper is not guessing. */
export const SITE_PAGE_HINTS: Record<SitePageKey, string> = {
  about: 'Who you are. Often the page that decides whether a stranger buys.',
  contact: 'How to reach a person. Your address and hours are already on /store.',
  delivery: 'Where you deliver and how long it takes.',
  returns: 'What happens if something comes back. Publishing this also puts your returns window into Google.',
  faq: 'Questions you are tired of answering on the phone. These can appear as rich results.',
  terms: 'Your terms of sale.',
  privacy: 'What you do with a customer’s phone number and address.',
};

/**
 * A body block. Four shapes and no free-form HTML: the storefront renders these
 * as React elements, so escaping is structural — there is nothing to sanitise
 * and no way to smuggle a script onto a domain every tenant shares.
 */
export type SitePageBlock =
  | { type: 'heading'; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'faq'; question: string; answer: string };

export type SitePageBlockType = SitePageBlock['type'];

export const BLOCK_LABELS: Record<SitePageBlockType, string> = {
  heading: 'Heading',
  paragraph: 'Paragraph',
  list: 'Bullet list',
  faq: 'Question & answer',
};

export interface SitePageContent {
  page_key: SitePageKey;
  title: string;
  body: SitePageBlock[];
  status: 'DRAFT' | 'PUBLISHED';
  published_at: string | null;
  meta_title: string | null;
  meta_description: string | null;
  /** The shopkeeper's own "publish this but keep it out of search". */
  noindex: boolean;
  /** The platform's hold. Visible but not editable here — a page suppressed
   *  with no visible reason is a support ticket. */
  admin_noindex: boolean;
  /** `noindex || admin_noindex`, computed by the API so nobody applies half. */
  effective_noindex: boolean;
  /** Platform view only. */
  admin_note?: string | null;
  created_at: string;
  updated_at: string;
}

export interface UpsertSitePageContent {
  title: string;
  body: SitePageBlock[];
  status?: 'DRAFT' | 'PUBLISHED';
  meta_title?: string;
  meta_description?: string;
  noindex?: boolean;
}

/** Blank blocks of each kind, for "add a…". */
export function emptyBlock(type: SitePageBlockType): SitePageBlock {
  switch (type) {
    case 'heading':
      return { type: 'heading', text: '' };
    case 'list':
      return { type: 'list', items: [''] };
    case 'faq':
      return { type: 'faq', question: '', answer: '' };
    default:
      return { type: 'paragraph', text: '' };
  }
}

/**
 * Is this block worth sending?
 *
 * A blank block fails the API's own validation (every text field has a minimum
 * length of 1), so dropping them here is the difference between "publish" and a
 * 400 the shopkeeper has to decode. An empty editor row is a normal state
 * halfway through writing, not a mistake.
 */
export function isBlockFilled(block: SitePageBlock): boolean {
  switch (block.type) {
    case 'heading':
    case 'paragraph':
      return block.text.trim().length > 0;
    case 'list':
      return block.items.some((item) => item.trim().length > 0);
    case 'faq':
      return block.question.trim().length > 0 && block.answer.trim().length > 0;
    default:
      return false;
  }
}

/** Trim, and drop the empty rows a list block accumulates while being typed. */
export function cleanBlocks(blocks: SitePageBlock[]): SitePageBlock[] {
  return blocks
    .map((block): SitePageBlock => {
      switch (block.type) {
        case 'heading':
        case 'paragraph':
          return { ...block, text: block.text.trim() };
        case 'list':
          return { ...block, items: block.items.map((i) => i.trim()).filter(Boolean) };
        case 'faq':
          return {
            ...block,
            question: block.question.trim(),
            answer: block.answer.trim(),
          };
        default:
          return block;
      }
    })
    .filter(isBlockFilled);
}
